/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

import { useMemo } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  Treemap,
  XAxis,
  YAxis,
  ZAxis,
} from 'recharts';
import { CircleHelp, Loader2, RefreshCw } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';

const SECTOR_COLORS = [
  '#06b6d4',
  '#22c55e',
  '#f59e0b',
  '#ef4444',
  '#8b5cf6',
  '#3b82f6',
  '#14b8a6',
  '#f97316',
  '#e11d48',
  '#84cc16',
  '#a855f7',
  '#64748b',
];

const PERIOD_OPTIONS = [
  { value: '1D', label: 'Day' },
  { value: '1W', label: 'Week' },
  { value: '1M', label: 'Month' },
  { value: '1Q', label: 'Quarter' },
  { value: '1Y', label: 'Year' },
];

const CHART_DESCRIPTIONS = {
  performance: 'Ranks sectors by median return for the selected period, making relative winners and laggards easy to compare.',
  breadth: 'Compares each sector by participation: the share of instruments advancing and the share trading above their 50-day moving average.',
  marketCap: 'Sizes each sector by total market cap. Tile accents show the selected-period sector return.',
  momentumValuation: 'Plots median return against average P/E ratio. Bubble size reflects the number of instruments in the sector.',
  positioning: 'Compares cap-weighted sector position with the median stock position in the 52-week range. 0% is near the low, 100% is near the high.',
  leaders: 'Highlights individual instruments driving the move: top gainers, top losers, and unusual volume names.',
  heatmap: 'Summarizes sector metrics in one table. Cell color intensity reflects the strength or risk of each metric.',
};

const GRID_STROKE = 'color-mix(in srgb, var(--color-border-default) 66%, transparent)';
const AXIS_STROKE = 'var(--color-text-secondary)';
const TOOLTIP_STYLE = {
  backgroundColor: 'var(--color-background-surface)',
  border: '1px solid var(--color-border-default)',
  borderRadius: 8,
  color: 'var(--color-text-primary)',
};
const TOOLTIP_LABEL_STYLE = { color: 'var(--color-primary)' };
const TOOLTIP_ITEM_STYLE = { color: 'var(--color-text-primary)', fontWeight: 600 };
const CHART_TOOLTIP_PROPS = {
  contentStyle: TOOLTIP_STYLE,
  labelStyle: TOOLTIP_LABEL_STYLE,
  itemStyle: TOOLTIP_ITEM_STYLE,
  wrapperClassName: 'sector-chart-tooltip',
};

function toNumber(value, fallback = 0) {
  if (value == null || value === '') return fallback;
  const num = Number(value);
  return Number.isFinite(num) ? num : fallback;
}

function formatCompact(value, currency = false) {
  if (value == null) return '-';
  const num = Number(value);
  if (!Number.isFinite(num)) return '-';
  const prefix = currency ? '$' : '';
  const abs = Math.abs(num);
  if (abs >= 1e12) return `${prefix}${(num / 1e12).toFixed(2)}T`;
  if (abs >= 1e9) return `${prefix}${(num / 1e9).toFixed(2)}B`;
  if (abs >= 1e6) return `${prefix}${(num / 1e6).toFixed(1)}M`;
  if (abs >= 1e3) return `${prefix}${(num / 1e3).toFixed(1)}K`;
  return `${prefix}${num.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

function formatPercent(value, signed = false) {
  if (value == null) return '-';
  const num = Number(value);
  if (!Number.isFinite(num)) return '-';
  const sign = signed && num > 0 ? '+' : '';
  return `${sign}${num.toFixed(2)}%`;
}

function formatNumber(value, decimals = 2) {
  if (value == null) return '-';
  const num = Number(value);
  if (!Number.isFinite(num)) return '-';
  return num.toLocaleString(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

function formatDateTime(value) {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function changeColor(value) {
  const num = toNumber(value, 0);
  if (num > 1.5) return 'var(--color-success)';
  if (num > 0) return 'color-mix(in srgb, var(--color-success) 82%, var(--color-primary))';
  if (num < -1.5) return 'var(--color-destructive)';
  if (num < 0) return 'var(--color-warning)';
  return 'var(--color-text-secondary)';
}

function positionColor(value) {
  const num = toNumber(value, 0);
  if (num >= 70) return 'var(--color-success)';
  if (num >= 40) return 'var(--color-warning)';
  return 'var(--color-destructive)';
}

function heatStyle(value, kind) {
  if (value == null) return { backgroundColor: 'rgba(148, 163, 184, 0.08)', color: '#94a3b8' };
  const num = Number(value);
  if (!Number.isFinite(num)) return { backgroundColor: 'rgba(148, 163, 184, 0.08)', color: '#94a3b8' };

  if (kind === 'change') {
    const intensity = Math.min(0.34, Math.abs(num) / 8);
    return num >= 0
      ? { backgroundColor: `rgba(34, 197, 94, ${0.10 + intensity})`, color: '#86efac' }
      : { backgroundColor: `rgba(239, 68, 68, ${0.10 + intensity})`, color: '#fca5a5' };
  }
  if (kind === 'highGood') {
    const intensity = Math.min(0.34, Math.max(0, num) / 100);
    return { backgroundColor: `rgba(20, 184, 166, ${0.10 + intensity})`, color: '#5eead4' };
  }
  if (kind === 'rsi') {
    if (num >= 70) return { backgroundColor: 'rgba(239, 68, 68, 0.25)', color: '#fca5a5' };
    if (num <= 30) return { backgroundColor: 'rgba(59, 130, 246, 0.24)', color: '#93c5fd' };
    return { backgroundColor: 'rgba(34, 197, 94, 0.15)', color: '#86efac' };
  }
  if (kind === 'lowGood') {
    const intensity = Math.min(0.28, Math.max(0, 100 - num) / 240);
    return { backgroundColor: `rgba(245, 158, 11, ${0.10 + intensity})`, color: '#fcd34d' };
  }
  return { backgroundColor: 'rgba(148, 163, 184, 0.12)', color: '#cbd5e1' };
}

function chartTooltipFormatter(value, name) {
  const label = String(name || '');
  if (label.includes('%') || label.includes('Change') || label.includes('Breadth') || label.includes('SMA')) {
    return [formatPercent(value, label.includes('Change')), label];
  }
  if (label.includes('Market Cap')) return [formatCompact(value, true), label];
  if (label.includes('P/E')) return [formatNumber(value, 1), label];
  if (label.includes('Volume')) return [formatNumber(value, 2), label];
  return [formatNumber(value, 2), label];
}

function Metric({ label, value, tone = '' }) {
  return (
    <Card className="rounded-lg">
      <CardContent className="p-3">
        <p className="text-xs text-textSecondary mb-1 truncate">{label}</p>
        <p className={`text-lg font-semibold truncate ${tone}`}>{value}</p>
      </CardContent>
    </Card>
  );
}

function ChartInfoTooltip({ children }) {
  return (
    <span className="sector-chart-info">
      <button
        type="button"
        className="sector-chart-info-button"
        aria-label="Chart explanation"
      >
        <CircleHelp className="h-3.5 w-3.5" />
      </button>
      <span className="sector-chart-info-tooltip" role="tooltip">
        {children}
      </span>
    </span>
  );
}

function ChartPanel({ title, description, children, className = '' }) {
  return (
    <Card className={`rounded-lg ${className}`}>
      <CardContent className="p-4">
        <div className="mb-3 flex items-center gap-2">
          <h2 className="text-sm font-semibold text-textPrimary">{title}</h2>
          {description && <ChartInfoTooltip>{description}</ChartInfoTooltip>}
        </div>
        {children}
      </CardContent>
    </Card>
  );
}

function EmptyPanel({ loading, error, onRetry }) {
  return (
    <Card className="rounded-lg">
      <CardContent className="p-8">
        <div className="flex min-h-[220px] flex-col items-center justify-center gap-3 text-center">
          {loading ? (
            <>
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
              <p className="text-sm text-textSecondary">Loading sector analytics...</p>
            </>
          ) : (
            <>
              <p className={error ? 'text-sm text-destructive' : 'text-sm text-textSecondary'}>
                {error || 'No sector analytics available for the current filters.'}
              </p>
              {error && (
                <Button variant="outline" size="sm" onClick={onRetry}>
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Retry
                </Button>
              )}
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function TreemapCell(props) {
  const { x, y, width, height, name, returnPct, size, count, color } = props;
  const fill = color || changeColor(returnPct);
  const returnColor = changeColor(returnPct);
  const showLabel = width > 72 && height > 38;
  const showDetails = width > 136 && height > 82;
  const showCount = width > 172 && height > 112;

  return (
    <g>
      <rect
        x={x}
        y={y}
        width={width}
        height={height}
        rx={4}
        ry={4}
        fill={fill}
        fillOpacity={0.32}
        stroke={returnColor}
        strokeOpacity={0.72}
        strokeWidth={1.5}
      />
      {showLabel && (
        <foreignObject
          x={x + 1}
          y={y + 1}
          width={Math.max(0, width - 2)}
          height={Math.max(0, height - 2)}
        >
          <div xmlns="http://www.w3.org/1999/xhtml" className="sector-treemap-label">
            <div className="sector-treemap-name">{name}</div>
            {showDetails && (
              <div className="sector-treemap-meta">
                <span>{formatCompact(size, true)}</span>
                <span className={toNumber(returnPct) >= 0 ? 'text-success' : 'text-destructive'}>
                  {formatPercent(returnPct, true)}
                </span>
              </div>
            )}
            {showCount && (
              <div className="sector-treemap-count">{formatCompact(count)} instruments</div>
            )}
          </div>
        </foreignObject>
      )}
    </g>
  );
}

function MomentumTooltip({ active, payload }) {
  const sector = payload?.[0]?.payload;
  if (!active || !sector) return null;

  return (
    <div className="sector-momentum-tooltip">
      <div className="sector-momentum-tooltip-title">
        <span
          className="sector-legend-dot"
          style={{ backgroundColor: sector.color }}
        />
        <span>{sector.sector}</span>
      </div>
      <div className="sector-momentum-tooltip-grid">
        <span>Avg P/E</span>
        <strong>{formatNumber(sector.x, 1)}</strong>
        <span>Median Return</span>
        <strong className={toNumber(sector.y) >= 0 ? 'text-success' : 'text-destructive'}>
          {formatPercent(sector.y, true)}
        </strong>
        <span>Instruments</span>
        <strong>{formatCompact(sector.count)}</strong>
      </div>
    </div>
  );
}

function RangePositionTooltip({ active, payload }) {
  const sector = payload?.[0]?.payload;
  if (!active || !sector) return null;

  return (
    <div className="sector-momentum-tooltip">
      <div className="sector-momentum-tooltip-title">
        <span
          className="sector-legend-dot"
          style={{ backgroundColor: positionColor(sector.rangePosition) }}
        />
        <span>{sector.sector}</span>
      </div>
      <div className="sector-momentum-tooltip-grid">
        <span>Cap-weighted</span>
        <strong>{formatPercent(sector.weightedRangePosition)}</strong>
        <span>Median stock</span>
        <strong>{formatPercent(sector.medianRangePosition)}</strong>
        <span>Average stock</span>
        <strong>{formatPercent(sector.avgRangePosition)}</strong>
        <span>Instruments</span>
        <strong>{formatCompact(sector.count)}</strong>
      </div>
    </div>
  );
}

function SectorBubbleLegend({ rows }) {
  return (
    <div className="sector-bubble-legend">
      {rows.map((sector) => (
        <span
          key={`legend-${sector.sector}`}
          className="sector-bubble-legend-item"
          title={sector.sector}
        >
          <span
            className="sector-legend-dot"
            style={{ backgroundColor: sector.color }}
          />
          <span>{sector.sector}</span>
        </span>
      ))}
    </div>
  );
}

function LeaderList({ title, rows, metricKey, formatter }) {
  return (
    <div className="min-w-0">
      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-textSecondary">{title}</h3>
      <div className="sector-leader-list">
        {rows.length === 0 && (
          <div className="px-3 py-2 text-sm text-textSecondary">No data</div>
        )}
        {rows.map((row) => (
          <div key={`${title}-${row.symbol}`} className="sector-leader-row grid grid-cols-[4.25rem_1fr_auto] items-center gap-2 px-3 py-2 text-sm">
            <span className="font-mono font-semibold text-primary">{row.symbol}</span>
            <span className="truncate text-textSecondary">{row.sector}</span>
            <span className={metricKey === 'change_pct' ? (toNumber(row[metricKey]) >= 0 ? 'text-success' : 'text-destructive') : 'text-textPrimary'}>
              {formatter(row[metricKey])}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function SectorVisuals({
  data,
  loading,
  error,
  period,
  onPeriodChange,
  onRetry,
}) {
  const sectors = useMemo(() => (
    (data?.sectors || []).map((sector, index) => ({
      ...sector,
      color: SECTOR_COLORS[index % SECTOR_COLORS.length],
      returnPct: toNumber(sector.median_change_pct ?? sector.avg_change_pct),
      breadth: toNumber(sector.gainers_pct),
      aboveSma50: toNumber(sector.above_sma50_pct),
      marketCap: toNumber(sector.total_market_cap),
      avgPe: toNumber(sector.avg_pe_ratio, null),
      avgRsi: toNumber(sector.avg_rsi_14, null),
      distHigh: toNumber(sector.avg_dist_from_52w_high, null),
      avgRangePosition: toNumber(sector.avg_52w_position_pct, null),
      medianRangePosition: toNumber(sector.median_52w_position_pct, null),
      weightedRangePosition: toNumber(sector.market_cap_weighted_52w_position_pct, null),
      rangePosition: toNumber(
        sector.market_cap_weighted_52w_position_pct
          ?? sector.median_52w_position_pct
          ?? sector.avg_52w_position_pct,
        null
      ),
      volumeRatio: toNumber(sector.avg_volume_ratio, null),
    }))
  ), [data]);

  const performanceData = useMemo(() => (
    [...sectors].sort((a, b) => b.returnPct - a.returnPct)
  ), [sectors]);

  const breadthData = useMemo(() => (
    [...sectors].sort((a, b) => b.breadth - a.breadth)
  ), [sectors]);

  const capData = useMemo(() => (
    sectors
      .filter((sector) => sector.marketCap > 0)
      .map((sector) => ({
        ...sector,
        name: sector.sector,
        size: sector.marketCap,
      }))
  ), [sectors]);

  const valuationData = useMemo(() => (
    sectors
      .filter((sector) => sector.avgPe != null && sector.avgPe > 0)
      .map((sector) => ({
        ...sector,
        x: Math.min(sector.avgPe, 120),
        y: sector.returnPct,
        z: Math.max(20, sector.count),
      }))
  ), [sectors]);

  const valuationLegendData = useMemo(() => (
    [...valuationData].sort((a, b) => a.sector.localeCompare(b.sector))
  ), [valuationData]);

  const positioningData = useMemo(() => (
    [...sectors]
      .filter((sector) => sector.rangePosition != null)
      .sort((a, b) => b.rangePosition - a.rangePosition)
  ), [sectors]);

  const summary = data?.summary || {};
  const leaders = data?.leaders || {};
  const selectedPeriod = data?.period?.key || period || '1D';
  const periodLabel = data?.period?.label || selectedPeriod;
  const asOfLabel = formatDateTime(data?.as_of);
  const periodHeader = (
    <div className="sector-period-toolbar flex flex-wrap items-center justify-between gap-3 px-3 py-2">
      <div className="flex flex-wrap items-center gap-1">
        {PERIOD_OPTIONS.map((option) => (
          <button
            key={option.value}
            type="button"
            onClick={() => onPeriodChange?.(option.value)}
            className={`min-w-[4.75rem] rounded-md px-3 py-1.5 text-xs font-semibold transition ${
              selectedPeriod === option.value
                ? 'bg-primary text-textAccent'
                : 'text-textSecondary hover:bg-primary/10 hover:text-textPrimary'
            }`}
          >
            {option.label}
          </button>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-3 text-xs text-textSecondary">
        <span>Return {periodLabel}</span>
        <span>As of {asOfLabel}</span>
      </div>
    </div>
  );

  if (loading || error || sectors.length === 0) {
    return (
      <div className="space-y-3">
        {periodHeader}
        <EmptyPanel loading={loading} error={error} onRetry={onRetry} />
      </div>
    );
  }

  const medianReturn = toNumber(summary.median_change_pct);
  const metricTone = medianReturn >= 0 ? 'text-success' : 'text-destructive';
  const heatMetrics = [
    { key: 'median_change_pct', label: 'Return', kind: 'change', format: (value) => formatPercent(value, true) },
    { key: 'gainers_pct', label: 'Breadth', kind: 'highGood', format: (value) => formatPercent(value) },
    { key: 'above_sma50_pct', label: '>SMA50', kind: 'highGood', format: (value) => formatPercent(value) },
    { key: 'avg_rsi_14', label: 'RSI', kind: 'rsi', format: (value) => formatNumber(value, 1) },
    { key: 'avg_volume_ratio', label: 'Vol', kind: 'highGood', format: (value) => formatNumber(value, 2) },
    { key: 'avg_pe_ratio', label: 'P/E', kind: 'lowGood', format: (value) => formatNumber(value, 1) },
    { key: 'market_cap_weighted_52w_position_pct', label: '52W Pos', kind: 'highGood', format: (value) => formatPercent(value) },
  ];

  return (
    <div className="space-y-3">
      {periodHeader}

      <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
        <Metric label="Sectors" value={formatCompact(summary.sector_count)} />
        <Metric label="Instruments" value={formatCompact(summary.instrument_count)} />
        <Metric label={`Median ${periodLabel}`} value={formatPercent(summary.median_change_pct, true)} tone={metricTone} />
        <Metric label="Advancing" value={formatPercent(summary.gainers_pct)} />
        <Metric label="Market Cap" value={formatCompact(summary.total_market_cap, true)} />
      </div>

      <div className="grid grid-cols-1 gap-3 xl:grid-cols-2">
        <ChartPanel title="Sector Performance" description={CHART_DESCRIPTIONS.performance}>
          <div className="h-[360px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={performanceData} layout="vertical" margin={{ top: 4, right: 18, bottom: 8, left: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} horizontal={false} />
                <XAxis type="number" stroke={AXIS_STROKE} tick={{ fontSize: 11 }} tickFormatter={(value) => `${value}%`} />
                <YAxis dataKey="sector" type="category" width={138} stroke={AXIS_STROKE} tick={{ fontSize: 11 }} />
                <Tooltip formatter={chartTooltipFormatter} {...CHART_TOOLTIP_PROPS} />
                <Bar dataKey="returnPct" name="Median Return %" radius={[0, 4, 4, 0]}>
                  {performanceData.map((entry) => (
                    <Cell key={`perf-${entry.sector}`} fill={changeColor(entry.returnPct)} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </ChartPanel>

        <ChartPanel title="Breadth and Trend" description={CHART_DESCRIPTIONS.breadth}>
          <div className="h-[360px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={breadthData} layout="vertical" margin={{ top: 4, right: 18, bottom: 8, left: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} horizontal={false} />
                <XAxis type="number" domain={[0, 100]} stroke={AXIS_STROKE} tick={{ fontSize: 11 }} tickFormatter={(value) => `${value}%`} />
                <YAxis dataKey="sector" type="category" width={138} stroke={AXIS_STROKE} tick={{ fontSize: 11 }} />
                <Tooltip formatter={chartTooltipFormatter} {...CHART_TOOLTIP_PROPS} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="gainers_pct" name="Breadth %" fill="#14b8a6" radius={[0, 4, 4, 0]} />
                <Bar dataKey="above_sma50_pct" name="Above SMA50 %" fill="#3b82f6" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </ChartPanel>
      </div>

      <div className="grid grid-cols-1 gap-3 xl:grid-cols-2">
        <ChartPanel title="Market Cap Map" description={CHART_DESCRIPTIONS.marketCap}>
          <div className="h-[340px]">
            {capData.length === 0 ? (
              <div className="flex h-full items-center justify-center text-sm text-textSecondary">No market cap data</div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <Treemap
                  data={capData}
                  dataKey="size"
                  aspectRatio={4 / 3}
                  stroke="color-mix(in srgb, var(--color-border-default) 28%, transparent)"
                  content={<TreemapCell />}
                >
                  <Tooltip
                    formatter={(value, name) => {
                      if (name === 'size') return [formatCompact(value, true), 'Market Cap'];
                      return chartTooltipFormatter(value, name);
                    }}
                    {...CHART_TOOLTIP_PROPS}
                  />
                </Treemap>
              </ResponsiveContainer>
            )}
          </div>
        </ChartPanel>

        <ChartPanel title="Momentum vs Valuation" description={CHART_DESCRIPTIONS.momentumValuation}>
          <div className="space-y-3">
            <div className="h-[320px]">
              {valuationData.length === 0 ? (
                <div className="flex h-full items-center justify-center text-sm text-textSecondary">No valuation data</div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <ScatterChart margin={{ top: 12, right: 22, bottom: 20, left: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} />
                    <XAxis
                      type="number"
                      dataKey="x"
                      name="Avg P/E"
                      stroke={AXIS_STROKE}
                      tick={{ fontSize: 11 }}
                      label={{ value: 'Avg P/E', position: 'insideBottom', offset: -10, fill: AXIS_STROKE, fontSize: 11 }}
                    />
                    <YAxis
                      type="number"
                      dataKey="y"
                      name="Median Return %"
                      stroke={AXIS_STROKE}
                      tick={{ fontSize: 11 }}
                      tickFormatter={(value) => `${value}%`}
                    />
                    <ZAxis type="number" dataKey="z" range={[90, 740]} />
                    <Tooltip
                      cursor={{ strokeDasharray: '3 3' }}
                      content={<MomentumTooltip />}
                      wrapperClassName="sector-chart-tooltip"
                    />
                    <Scatter name="Sectors" data={valuationData}>
                      {valuationData.map((entry) => (
                        <Cell key={`scatter-${entry.sector}`} fill={entry.color} />
                      ))}
                    </Scatter>
                  </ScatterChart>
                </ResponsiveContainer>
              )}
            </div>
            {valuationLegendData.length > 0 && <SectorBubbleLegend rows={valuationLegendData} />}
          </div>
        </ChartPanel>
      </div>

      <div className="grid grid-cols-1 gap-3 xl:grid-cols-2">
        <ChartPanel title="52-Week Range Position" description={CHART_DESCRIPTIONS.positioning}>
          <div className="h-[330px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={positioningData} layout="vertical" margin={{ top: 4, right: 18, bottom: 8, left: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} horizontal={false} />
                <XAxis type="number" domain={[0, 100]} stroke={AXIS_STROKE} tick={{ fontSize: 11 }} tickFormatter={(value) => `${value}%`} />
                <YAxis dataKey="sector" type="category" width={138} stroke={AXIS_STROKE} tick={{ fontSize: 11 }} />
                <Tooltip
                  content={<RangePositionTooltip />}
                  wrapperClassName="sector-chart-tooltip"
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="weightedRangePosition" name="Cap-weighted %" radius={[0, 4, 4, 0]}>
                  {positioningData.map((entry) => (
                    <Cell key={`position-${entry.sector}`} fill={positionColor(entry.rangePosition)} />
                  ))}
                </Bar>
                <Bar
                  dataKey="medianRangePosition"
                  name="Median stock %"
                  fill="color-mix(in srgb, var(--color-primary) 64%, var(--color-text-secondary))"
                  radius={[0, 4, 4, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </ChartPanel>

        <ChartPanel title="Sector Leaders" description={CHART_DESCRIPTIONS.leaders}>
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
            <LeaderList
              title="Top Gainers"
              rows={leaders.top_gainers || []}
              metricKey="change_pct"
              formatter={(value) => formatPercent(value, true)}
            />
            <LeaderList
              title="Top Losers"
              rows={leaders.top_losers || []}
              metricKey="change_pct"
              formatter={(value) => formatPercent(value, true)}
            />
            <LeaderList
              title="Unusual Volume"
              rows={leaders.unusual_volume || []}
              metricKey="volume_ratio"
              formatter={(value) => `${formatNumber(value, 2)}x`}
            />
          </div>
        </ChartPanel>
      </div>

      <ChartPanel title="Sector Heatmap" description={CHART_DESCRIPTIONS.heatmap}>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[760px] text-sm">
            <thead>
              <tr className="sector-heatmap-head text-xs text-textSecondary">
                <th className="px-3 py-2 text-left font-semibold">Sector</th>
                <th className="px-3 py-2 text-right font-semibold">Count</th>
                {heatMetrics.map((metric) => (
                  <th key={metric.key} className="px-3 py-2 text-right font-semibold">{metric.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {performanceData.map((sector) => (
                <tr key={`heat-${sector.sector}`} className="sector-heatmap-row">
                  <td className="max-w-[220px] truncate px-3 py-2 font-medium text-textPrimary">{sector.sector}</td>
                  <td className="px-3 py-2 text-right font-mono text-textSecondary">{formatCompact(sector.count)}</td>
                  {heatMetrics.map((metric) => (
                    <td key={`${sector.sector}-${metric.key}`} className="px-2 py-1.5 text-right">
                      <span
                        className="inline-flex min-w-[72px] justify-end rounded px-2 py-1 font-mono text-xs"
                        style={heatStyle(sector[metric.key], metric.kind)}
                      >
                        {metric.format(sector[metric.key])}
                      </span>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </ChartPanel>
    </div>
  );
}
